import React, { useCallback, useMemo, useState } from 'react';
import { useDorisTableData } from 'src/serverProxy/useDorisTableData';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import useProductMetaData from 'src/components/AtlasV2Components/Hooks/useProductMetaData';
import { ProductEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/types';
import { DorisGroupByFieldNameEnum } from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { GridSortOptions } from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';

interface Item {
  fieldKey: string;
  metadata: {
    retailerId?: number;
    stacklineSku?: string;
    name?: string;
  };
  name: string;
  [key: string]: string | number | boolean | object | undefined;
}

interface EnrichedItem extends Omit<Item, 'metadata'> {
  metadata: Item['metadata'] & ProductEntity;
}

export function getSkusForPagination({
  data,
  currentPage,
  ITEMS_PER_PAGE,
  METADATA_FETCH_SIZE
}: {
  data: Item[];
  currentPage: number;
  ITEMS_PER_PAGE: number;
  METADATA_FETCH_SIZE: number;
}): string[] {
  const startChunk = Math.floor(((currentPage - 1) * ITEMS_PER_PAGE) / METADATA_FETCH_SIZE);
  const endChunk = Math.floor((currentPage * ITEMS_PER_PAGE - 1) / METADATA_FETCH_SIZE);

  const allSkus: string[] = [];
  for (let i = startChunk; i <= endChunk; i++) {
    const chunkStart = i * METADATA_FETCH_SIZE;
    const chunkEnd = chunkStart + METADATA_FETCH_SIZE;

    const chunkSkus = data
      .slice(chunkStart, chunkEnd)
      .map((item) => item.fieldKey || item.stacklineSku)
      .filter(Boolean);

    allSkus.push(...chunkSkus);
  }

  return allSkus;
}

interface UsePaginatedProductDataProps {
  apiMethodName: string;
  groupByFieldName: string;
  metricType: string;
  additionalParams?: Record<string, any>;
  fields: Array<{
    name: string;
    displayName: string;
    wrapperStyle?: React.CSSProperties;
  }>;
  itemsPerPage?: number;
  metadataFetchSize?: number;
  sortFieldName?: string;
  sortOption?: GridSortOptions;
}

interface UsePaginatedProductDataReturn {
  // Data
  enrichedData: EnrichedItem[];
  slicedData: Item[];
  data: Item[];
  productMetadata: { [key: string]: ProductEntity };

  isLoading: boolean;
  isMetadataLoading: boolean;

  currentPage: number;
  setCurrentPage: (page: number) => void;

  handleNextPage: () => void;
  handlePreviousPage: () => void;

  getPagedValuesByFieldName: (fieldName: string) => (
    | string
    | number
    | {
        name: string;
        stacklineSku: string;
      }
  )[];
  getPagedValueDifferencesByFieldName: (fieldName: string) => number[];

  // Raw functions from useDorisTableData
  getValuesByFieldName: (fieldName: string) => (
    | string
    | number
    | {
        name: string;
        stacklineSku: string;
      }
  )[];
  getValueDifferencesByFieldName: (fieldName: string) => number[];

  // Configuration
  ITEMS_PER_PAGE: number;
  METADATA_FETCH_SIZE: number;

  // Total count after filtering
  totalFilteredCount: number;
}

/**
 * Custom hook that handles paginated product data with metadata enrichment
 * Extracts all the common pagination, data slicing, SKU fetching, and metadata logic
 * that's repeated across multiple product grid components.
 */
export const usePaginatedProductData = ({
  apiMethodName,
  groupByFieldName,
  metricType,
  additionalParams,
  fields,
  sortFieldName,
  sortOption,
  itemsPerPage = 20,
  metadataFetchSize = 100
}: UsePaginatedProductDataProps): UsePaginatedProductDataReturn => {
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = itemsPerPage;
  const METADATA_FETCH_SIZE = metadataFetchSize;
  const retailerId = useRetailerId();
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);

  // Note: useDorisTableData internally multiplies itemsPerPage by 50 for the resultCount
  // So when we pass itemsPerPage=20, it fetches 1000 items (20 * 50)
  // This ensures we always have enough data for client-side filtering without refetching
  const { data, isLoading, getValuesByFieldName, getValueDifferencesByFieldName } = useDorisTableData({
    apiMethodName,
    itemsPerPage: ITEMS_PER_PAGE,
    groupByFieldName,
    metricType,
    fields,
    additionalParams: {
      ...additionalParams
    }
  });

  // Filter and sort data based on sort option
  const filteredData = useMemo(() => {
    if (isLoading || !data) {
      return [];
    }

    if (sortOption && sortFieldName) {
      // Get values and differences for the sort field
      const values = getValuesByFieldName(sortFieldName);
      const differences = getValueDifferencesByFieldName(sortFieldName);

      // Create indexed data with values and differences
      const indexedData = data.map((item, index) => ({
        item,
        index,
        value: values[index],
        difference: differences[index]
      }));

      return indexedData
        .sort((a, b) => {
          switch (sortOption) {
            case GridSortOptions.TopSelling:
              return (Number(b.value) || 0) - (Number(a.value) || 0);
            case GridSortOptions.Increasing:
              return (Number(b.difference) || 0) - (Number(a.difference) || 0);
            case GridSortOptions.Decreasing:
              return (Number(a.difference) || 0) - (Number(b.difference) || 0);
            default:
              return 0;
          }
        })
        .filter((d) => {
          if (sortOption === GridSortOptions.Increasing) {
            return Number(d.difference) >= 0;
          } else if (sortOption === GridSortOptions.Decreasing) {
            return Number(d.difference) <= 0;
          }
          return true;
        })
        .map((d) => d.item);
    }

    return data;
  }, [data, isLoading, sortOption, sortFieldName, getValuesByFieldName, getValueDifferencesByFieldName]);

  // Slice data based on current page
  const slicedData = useMemo(() => {
    if (isLoading) {
      return [];
    }

    // Use filtered data if available
    const dataToSlice = filteredData.length > 0 ? filteredData : data;

    // Special case: Week data needs to be sorted by weekId
    if (groupByFieldName === DorisGroupByFieldNameEnum.Week) {
      return dataToSlice
        .sort((a, b) => b.weekId - a.weekId)
        .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    }

    // If we have a sortOption, the data is already sorted in filteredData
    // Don't re-sort it here!
    if (sortOption && filteredData.length > 0) {
      return dataToSlice.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    }

    // Only apply default sorting if no sortOption is active
    if (sortFieldName && !sortOption) {
      return dataToSlice
        .sort((a, b) => b[sortFieldName] - a[sortFieldName])
        .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    }

    return dataToSlice.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [isLoading, groupByFieldName, sortFieldName, sortOption, data, currentPage, ITEMS_PER_PAGE, filteredData]);

  // Extract SKUs for metadata fetching
  const skusToFetch = useMemo(() => {
    // Use filtered data if available, otherwise use original data
    const dataSource = filteredData.length > 0 ? filteredData : data;

    return getSkusForPagination({
      data: dataSource,
      currentPage,
      ITEMS_PER_PAGE,
      METADATA_FETCH_SIZE
    });
  }, [data, filteredData, currentPage, ITEMS_PER_PAGE, METADATA_FETCH_SIZE]);

  // Fetch product metadata
  const { data: productMetadata, isLoading: isMetadataLoading } = useProductMetaData({
    skusToFetch,
    retailerId: Number(retailerId),
    enabled:
      skusToFetch.length > 0 &&
      [DorisGroupByFieldNameEnum.Product, DorisGroupByFieldNameEnum.Week].includes(
        groupByFieldName as DorisGroupByFieldNameEnum
      )
  });

  const entityMap = useMemo(() => {
    if (groupByFieldName === DorisGroupByFieldNameEnum.Product) {
      return productMetadata;
    }

    if (groupByFieldName === DorisGroupByFieldNameEnum.Category) {
      return categories.reduce((acc, category) => {
        if (category.categoryId !== undefined) {
          acc[category.categoryId] = category;
        }
        return acc;
      }, {});
    }
    if (groupByFieldName === DorisGroupByFieldNameEnum.Subcategory) {
      return subCategories.reduce((acc, subCategory) => {
        if (subCategory.subCategoryId !== undefined) {
          acc[subCategory.subCategoryId] = subCategory;
        }
        return acc;
      }, {});
    }
  }, [groupByFieldName, categories, subCategories, productMetadata]);

  // Enrich data with metadata
  const enrichedData = useMemo(() => {
    return slicedData.map((item) => ({
      ...item,
      fieldKey: item.fieldKey || item.stacklineSku,
      metadata: {
        ...(groupByFieldName === DorisGroupByFieldNameEnum.Product ||
        groupByFieldName === DorisGroupByFieldNameEnum.Week
          ? { product: productMetadata?.[item.fieldKey || item.stacklineSku] }
          : {}),
        ...(groupByFieldName === DorisGroupByFieldNameEnum.Category ? { category: entityMap[item.fieldKey] } : {}),
        ...(groupByFieldName === DorisGroupByFieldNameEnum.Subcategory
          ? { subcategory: entityMap[item.fieldKey] }
          : {}),
        ...(groupByFieldName === DorisGroupByFieldNameEnum.Brand
          ? { brand: { ...item.metadata, name: item.metadata.brandName } }
          : {})
      }
    }));
  }, [slicedData, productMetadata, entityMap, groupByFieldName]);

  // Paged value helper functions
  const getPagedValuesByFieldName = useCallback(
    (fieldName: string) => {
      const values = getValuesByFieldName(fieldName);

      // If we have filtered data, we need to get values for the filtered items
      if (filteredData.length > 0 && sortOption) {
        // Get indices of items in the current page slice
        const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIdx = currentPage * ITEMS_PER_PAGE;
        const pageItems = filteredData.slice(startIdx, endIdx);

        // Find original indices of these items in the data array
        return pageItems.map((item) => {
          const originalIndex = data.findIndex((d) => d === item);
          return values[originalIndex];
        });
      }

      return values.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    },
    [currentPage, getValuesByFieldName, ITEMS_PER_PAGE, filteredData, sortOption, data]
  );

  const getPagedValueDifferencesByFieldName = useCallback(
    (fieldName: string) => {
      const differences = getValueDifferencesByFieldName(fieldName);

      // If we have filtered data, we need to get differences for the filtered items
      if (filteredData.length > 0 && sortOption) {
        // Get indices of items in the current page slice
        const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIdx = currentPage * ITEMS_PER_PAGE;
        const pageItems = filteredData.slice(startIdx, endIdx);

        // Find original indices of these items in the data array
        return pageItems.map((item) => {
          const originalIndex = data.findIndex((d) => d === item);
          return differences[originalIndex];
        });
      }

      return differences.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    },
    [currentPage, getValueDifferencesByFieldName, ITEMS_PER_PAGE, filteredData, sortOption, data]
  );

  // Navigation handlers
  const handleNextPage = useCallback(() => {
    const totalItems = filteredData.length > 0 ? filteredData.length : data.length;
    const maxPage = Math.ceil(totalItems / ITEMS_PER_PAGE);

    if (currentPage < maxPage) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  }, [currentPage, filteredData.length, data.length, ITEMS_PER_PAGE]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  }, [currentPage]);

  return {
    enrichedData: enrichedData as unknown as EnrichedItem[],
    slicedData,
    data,
    productMetadata: productMetadata as unknown as { [key: string]: ProductEntity },

    isLoading,
    isMetadataLoading,

    currentPage,
    setCurrentPage,

    handleNextPage,
    handlePreviousPage,

    getPagedValuesByFieldName,
    getPagedValueDifferencesByFieldName,

    getValuesByFieldName,
    getValueDifferencesByFieldName,

    ITEMS_PER_PAGE,
    METADATA_FETCH_SIZE,

    // Total count after filtering
    totalFilteredCount: filteredData.length
  };
};
